import { Component, ErrorInfo, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography, Button, alpha } from '@mui/material';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

interface IErrorBoundaryProps {
  children: ReactNode;
}
interface IErrorBoundaryState {
  error: Error | null;
}

// Sem isto, um erro não tratado durante o render de QUALQUER tela derruba a árvore React
// inteira (comportamento padrão do React >=16 sem error boundary) — a SPA trava por completo,
// inclusive o menu lateral, impedindo até de navegar pra outra rota. Captura o erro aqui em
// vez de deixar propagar: mostra só a mensagem, sem travar o resto do sistema.
class ErrorBoundaryBase extends Component<IErrorBoundaryProps, IErrorBoundaryState> {
  state: IErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): IErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Erro não tratado ao renderizar a tela:', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={2}
          sx={{ height: '100%', minHeight: 360, p: 4, textAlign: 'center' }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: (theme) => alpha(theme.palette.error.main, 0.12),
            }}
          >
            <ErrorOutlineRoundedIcon color="error" sx={{ fontSize: 34 }} />
          </Box>
          <Typography variant="h6" fontWeight={700}>Não foi possível carregar esta tela</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
            {this.state.error.message || 'Ocorreu um erro inesperado.'}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshRoundedIcon />}
            onClick={() => window.location.reload()}
            sx={{ textTransform: 'none' }}
          >
            Recarregar página
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

// Troca a `key` da boundary a cada rota — sem isto, uma vez capturado o erro, o estado fica
// preso e a tela de erro continua aparecendo mesmo depois de navegar pra outra rota (a
// boundary não remonta sozinha só porque as rotas mudaram por baixo dela).
export const ErrorBoundary: React.FC<IErrorBoundaryProps> = ({ children }) => {
  const { pathname } = useLocation();
  return <ErrorBoundaryBase key={pathname}>{children}</ErrorBoundaryBase>;
};
